import React, { useEffect, useRef, useState, useContext, useCallback } from 'react';
import WebsiteForm from '../../views/website/WebsiteForm';
import WebsiteFiles from '../../views/website/WebsiteFiles';
import CommentTable from '../../views/website/WebsiteComments';
import WebsiteTeam from '../../views/website/WebsiteTeam';
import { WebsiteContext } from '../../context/WebsiteProvider';
import { UserContext } from '../../context/UserProvider';
import { FaInfoCircle, FaFile, FaHistory, FaExclamationCircle } from 'react-icons/fa';
import { RiTeamFill } from "react-icons/ri";
import recip from '../../assets/icons/recip.png';
import WebsiteHistory from '../../views/website/WebsiteHistory';

const WebsiteModal = ({ onClose, website, vendorLinkDisabled }) => {
  const {
    websiteData,
    setWebsiteData,
    getActivity,
    setSelectedWebsite,
    hasHistory,
    setHasHistory,
    postComment,
    isTeamGroupWebsite,
    setIsTeamGroupWebsite,
  } = useContext(WebsiteContext);

  const { isExpanded: globalIsExpanded } = useContext(UserContext);

  const [isExpanded, setIsExpanded] = useState(true);
  const modalRef = useRef();
  const formRef = useRef(null);
  const commentsRef = useRef(null);
  const historyRef = useRef(null);
  const filesRef = useRef(null);
  const teamRef = useRef(null);
  const sidebarRef = useRef(null);
  const [selectedLink, setSelectedLink] = useState('website-form');
  const [commentText, setCommentText] = useState('');
  const [isPublic, setIsPublic] = useState(false);

  useEffect(() => {
    setIsExpanded(globalIsExpanded);
  }, [globalIsExpanded]);

  const resetModalState = () => {
    if (formRef.current && formRef.current.resetForm) {
      formRef.current.resetForm();
    }
    if (formRef.current && formRef.current.resetVendorModal) {
      formRef.current.resetVendorModal(); // Reset isVendorOpen in WebsiteForm
    }
    if (commentsRef.current && commentsRef.current.resetComments) {
      commentsRef.current.resetComments();
    }
  };

  const handleClose = useCallback(() => {
    resetModalState();
    if (onClose) {
      onClose();
    }
    setWebsiteData(null);
  }, [onClose, setWebsiteData]);

  useEffect(() => {
    setWebsiteData(website);
    setSelectedWebsite(website);
    if (website?.products && website.products.includes(2)) {
      setIsTeamGroupWebsite(true);
    }
  }, [website, setWebsiteData, setSelectedWebsite, setIsTeamGroupWebsite]);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && entry.intersectionRatio >= 0.3) {
            setSelectedLink(entry.target.id);
          }
        });
      },
      {
        root: document.querySelector('.scroll-snap-container'),
        threshold: 0.3,
        rootMargin: '0px'
      }
    );

    const sections = [formRef, commentsRef, filesRef, historyRef, teamRef];
    sections.forEach((section) => {
      if (section.current) {
        observer.observe(section.current);
      }
    });

    return () => {
      sections.forEach((section) => {
        if (section.current) {
          observer.unobserve(section.current);
        }
      });
    };
  }, [websiteData?.rowKey, hasHistory]);

  useEffect(() => {
    const fetchHistory = async () => {
      const history = await getActivity(website?.rowKey);
      setHasHistory(history && Array.isArray(history) && history.length > 0);
    };
    if (website?.rowKey) {
      fetchHistory();
    }
  }, [website?.rowKey, getActivity, setHasHistory]);

  const scrollToComponent = (ref) => {
    if (ref && ref.current) {
      const container = document.querySelector('.scroll-snap-container');
      const headerHeight = 60;
      const offsetAdjustment = 150;
      const elementPosition = ref.current.offsetTop;
      const offsetPosition = elementPosition - headerHeight - offsetAdjustment;
      if (container) {
        container.scrollTo({
          top: offsetPosition,
          behavior: 'smooth',
        });
      }
    }
  };

  const getVisibleSections = () => {
    const allSections = [formRef, filesRef, historyRef, teamRef];
    return allSections.filter(ref => {
      if (!ref.current) return false;
      if (ref === filesRef && !websiteData?.rowKey) return false;
      if (ref === historyRef && (!websiteData?.rowKey || !hasHistory)) return false;
      if (ref === teamRef && (!websiteData?.rowKey || !isTeamGroupWebsite)) return false;
      return true;
    });
  };

  const handleSidebarScroll = (e) => {
    e.preventDefault();
    const delta = e.deltaY;
    const sections = getVisibleSections();
    const container = document.querySelector('.scroll-snap-container');
    if (!container || sections.length === 0) return;

    const currentScroll = container.scrollTop;
    let currentIndex = sections.findIndex(ref => {
      const top = ref.current.offsetTop - 210;
      const bottom = top + ref.current.offsetHeight;
      return currentScroll >= top && currentScroll < bottom;
    });

    if (currentIndex === -1) {
      currentIndex = 0;
    }

    let nextIndex;
    if (delta > 0) {
      nextIndex = Math.min(currentIndex + 1, sections.length - 1);
    } else {
      nextIndex = Math.max(currentIndex - 1, 0);
    }

    if (sections[nextIndex]) {
      scrollToComponent(sections[nextIndex]);
    }
  };

  useEffect(() => {
    const sidebar = sidebarRef.current;
    if (sidebar) {
      sidebar.addEventListener('wheel', handleSidebarScroll, { passive: false });
    }
    return () => {
      if (sidebar) {
        sidebar.removeEventListener('wheel', handleSidebarScroll);
      }
    };
  }, [websiteData?.rowKey, hasHistory, isTeamGroupWebsite]);

  const modalStyle =
    'fixed inset-0 bg-white dark:bg-stone-900 z-50 pt-20 transition-all duration-500 ease-in-out';

  return (
    <div
      id="website-modal"
      className="fixed inset-0 z-50 flex items-start justify-center bg-stone-950 bg-opacity-80 pt-10 dark:bg-opacity-95"
    >
      <div ref={modalRef} className={modalStyle}>
        <div className="flex items-center justify-between border-b border-stone-200 px-4 py-2 dark:border-stone-600">
          <div style={{display: 'flex', alignItems: 'center'}}>
            <h2 className="text-lg font-bold dark:text-gray-200">
              {websiteData ? websiteData.domainName : 'New Website'}
            </h2>
            {websiteData?.reciprocal && (
              <img
                title="Reciprocal"
                src={recip}
                alt="Reciprocal"
                className="h-4 w-4 recip"
                style={{marginLeft: '15px'}}
              />
            )}
          </div>
          <div className="flex items-center">
            <button onClick={handleClose}>
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="h-6 w-6 text-gray-600 hover:text-gray-800 dark:text-gray-500 dark:hover:text-gray-700"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>
          </div>
        </div>
        <div className="flex h-full">
          <div
            ref={sidebarRef}
            className="sidebar-navigation flex h-full w-40 flex-col overflow-y-auto border-r bg-white px-6 py-6 dark:border-stone-600 dark:bg-stone-800"
          >
            <ul className="list-none">
              <li className="mb-4">
                <button
                  onClick={() => scrollToComponent(formRef)}
                  title="General Information"
                  className={`flex min-w-full items-center p-1.5 text-center font-semibold text-blue-900 transition-colors duration-300 hover:text-blue-700 dark:text-indigo-200 ${
                    selectedLink === 'website-form'
                      ? 'rounded bg-blue-100 dark:bg-stone-600'
                      : ''
                  }`}
                >
                  <FaInfoCircle className="mr-2"/>
                  <span className="block">General</span>
                </button>
              </li>
              {websiteData?.rowKey && (
                <li className="mb-4">
                  <button
                    onClick={() => scrollToComponent(filesRef)}
                    title="Files"
                    className={`flex min-w-full items-center p-1.5 text-left font-semibold text-blue-900 transition-colors duration-300 hover:text-blue-700 dark:text-indigo-200 ${
                      selectedLink === 'website-files'
                        ? 'rounded bg-blue-100 dark:bg-stone-600'
                        : ''
                    }`}
                  >
                    {websiteData?.documentsWaiting ? (
                      <FaExclamationCircle
                        title="Documents Pending"
                        className="doc-pending-icon-standout mr-2 h-4 w-4 text-orange-400 dark:text-yellow-400"
                      />
                    ) : (
                      <FaFile className="mr-2"/>
                    )}
                    <span className="block">Files</span>
                  </button>
                </li>
              )}
              {websiteData?.rowKey && hasHistory && (
                <li className="mb-4">
                  <button
                    onClick={() => scrollToComponent(historyRef)}
                    title="History"
                    className={`flex min-w-full items-center p-1.5 text-left font-semibold text-blue-900 transition-colors duration-300 hover:text-blue-700 dark:text-indigo-200 ${
                      selectedLink === 'website-history'
                        ? 'rounded bg-blue-100 dark:bg-stone-600'
                        : ''
                    }`}
                  >
                    <FaHistory className="mr-2"/>
                    <span className="block">History</span>
                  </button>
                </li>
              )}
              {websiteData?.rowKey && isTeamGroupWebsite && (
                <li className="mb-4">
                  <button
                    onClick={() => scrollToComponent(teamRef)}
                    title="Team"
                    className={`flex min-w-full items-center p-1.5 text-left font-semibold text-blue-900 transition-colors duration-300 hover:text-blue-700 dark:text-indigo-200 ${
                      selectedLink === 'website-team'
                        ? 'rounded bg-blue-100 dark:bg-stone-600'
                        : ''
                    }`}
                  >
                    <RiTeamFill className="mr-2"/>
                    <span className="block">Team</span>
                  </button>
                </li>
              )}
            </ul>
            <div className="ml-3 mt-auto w-32">
              <button
                onClick={handleClose}
                title="Close Window"
                className="mb-16 z-50 block items-center justify-center p-1.5 text-center text-xs font-bold text-blue-900 transition-colors duration-300 hover:text-blue-700 dark:text-indigo-400"
              >
                Close Window
              </button>
            </div>
          </div>

          <div className="ml-1/8 scroll-snap-container h-full w-full overflow-y-auto px-4 py-4">
            <div className="flex">
              <div
                ref={formRef}
                id="website-form"
                className={`scroll-snap-section`}
              >
                <div className="mt-8">
                  <WebsiteForm
                    websiteData={websiteData}
                    website={websiteData}
                    postComment={postComment}
                    commentText={commentText}
                    isPublic={isPublic}
                    setCommentText={setCommentText}
                    setIsPublic={setIsPublic}
                    vendorLinkDisabled={vendorLinkDisabled}
                    hasHistory={hasHistory}
                    onClose={handleClose}
                    setHasHistory={setHasHistory}
                    hideFirstComment={isExpanded}
                    resetVendorModal // Pass resetVendorModal prop
                  />
                </div>
              </div>
              <div id="comment-wrapper" style={{flex: '1 1 600px'}}>
                <CommentTable
                  WebsiteID={websiteData?.rowKey}
                  commentText={commentText}
                  setCommentText={setCommentText}
                  isPublic={isPublic}
                  setIsPublic={setIsPublic}
                />
              </div>
            </div>
            <div
              ref={filesRef}
              id="website-files"
              className={`scroll-snap-section min-w-[800px] ${
                websiteData?.rowKey ? '' : 'hidden'
              }`}
            >
              {websiteData?.rowKey && (
                <WebsiteFiles websiteId={websiteData.rowKey}/>
              )}
            </div>
            {hasHistory && (
              <div
                ref={historyRef}
                id="website-history"
                className={`scroll-snap-section min-w-[800px] ${
                  websiteData?.rowKey ? '' : 'hidden'
                }`}
              >
                {websiteData?.rowKey && (
                  <WebsiteHistory websiteId={websiteData.rowKey}/>
                )}
              </div>
            )}
            <div
              ref={teamRef}
              id="website-team"
              className={`scroll-snap-section min-w-[800px] ${
                websiteData?.rowKey && isTeamGroupWebsite ? '' : 'hidden'
              }`}
            >
              {websiteData?.rowKey && isTeamGroupWebsite && (
                <WebsiteTeam
                  websiteId={websiteData.rowKey}
                  reciprocalAllowed={websiteData?.reciprocalAllowed}
                />
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default WebsiteModal;